import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "../../lib/api";
import PublicShell from "../../components/PublicShell";
import HeroSearch from "../../components/HeroSearch";
import HouseCard from "../../components/HouseCard";
import LeadCaptureBanner from "../../components/LeadCaptureBanner";
import usePageSeo, { lodgingBusinessSchema, organizationSchema } from "../../lib/seo";
import { ArrowRight, MapPin, ShieldCheck, CreditCard, FileText, ClipboardCheck, Home as HomeIcon, MessageSquare } from "lucide-react";

function slugTestId(s) {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "-");
}

const DESTINATIONS = [
  { key: "Praia dos Carneiros", label: "Praia dos Carneiros" },
  { key: "Tamandaré", label: "Tamandaré" },
  { key: "Gravatá", label: "Gravatá" },
  { key: "Recife", label: "Recife" },
];

function pickHeroPhoto(houses) {
  // First house that has a photo — cover preferred. Returns null if none.
  for (const h of houses) {
    const ph = h.photos || [];
    if (ph.length === 0) continue;
    const cover = ph.find((p) => p.is_cover) || ph[0];
    if (cover?.storage_path) return { photo: cover, house: h };
  }
  return null;
}

export default function Home() {
  usePageSeo({
    title: "Casas de férias em Pernambuco",
    description:
      "Casas selecionadas no litoral e na serra de Pernambuco. Reserva direta, atendimento personalizado e uma jornada segura do pagamento ao check-out.",
    path: "/",
    jsonLd: [organizationSchema(), lodgingBusinessSchema()],
  });
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = city ? `?city=${encodeURIComponent(city)}` : "";
    api.get(`/public/houses${params}`).then((r) => {
      setHouses(r.data);
      setLoading(false);
    });
  }, [city]);

  const featured = houses.slice(0, 6);
  // Hero photo é sempre da lista sem filtro para não ficar vazio ao filtrar por cidade.
  const [allHouses, setAllHouses] = useState([]);
  useEffect(() => {
    api.get("/public/houses").then((r) => setAllHouses(r.data)).catch(() => {});
  }, []);
  const heroPick = pickHeroPhoto(allHouses);

  return (
    <PublicShell>
      {/* -------------------- Hero (com foto real) -------------------- */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, #F7F4EF 0%, #E9F5F3 60%, #DDF0EE 100%)" }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-28 md:pt-20 md:pb-36">
          <div className={`grid ${heroPick ? "lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]" : "grid-cols-1"} gap-10 lg:gap-14 items-center`}>
            {/* Texto */}
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-[#066B63] font-semibold mb-6">
                <span className="w-6 h-px bg-[#066B63]/60" aria-hidden="true" />
                Casas selecionadas em Pernambuco
              </span>
              <h1 className="font-display text-5xl md:text-6xl tracking-tight text-[#22302E] leading-[1.06]">
                Sua próxima estadia <br />
                em <span className="text-[#0AA89C]">Pernambuco</span> <br />
                começa aqui.
              </h1>
              <p className="mt-8 text-[17px] md:text-lg text-[#22302E]/80 max-w-xl leading-relaxed">
                Casas selecionadas no litoral e na serra de Pernambuco, com reserva direta, atendimento personalizado e uma jornada segura do pagamento ao check-out.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/casas"
                  data-testid="hero-primary-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#0AA89C] text-white rounded-full text-sm font-medium hover:bg-[#088c82] transition-colors shadow-sm"
                >
                  Ver casas disponíveis <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/reserva-segura"
                  data-testid="hero-reserva-segura-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-[#0AA89C]/70 text-[#066B63] rounded-full text-sm hover:bg-[#0AA89C]/5 transition-colors"
                >
                  <ShieldCheck className="w-4 h-4" /> Como funciona a Reserva Segura
                </Link>
              </div>
            </div>

            {/* Foto real da casa em destaque — só renderiza quando há foto */}
            {heroPick && (
            <div
              className="relative aspect-[4/5] lg:aspect-[3/4] rounded-2xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(6,107,99,0.35)]"
              data-testid="hero-image"
            >
              <img
                src={fileUrl(heroPick.photo.storage_path)}
                alt={heroPick.photo.alt_text || heroPick.house.name}
                className="w-full h-full object-cover"
                loading="eager"
              />
              {/* Overlay sutil + legenda com nome da casa */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#22302E]/70 via-transparent to-transparent" />
              <Link
                to={`/casas/${heroPick.house.slug}`}
                className="absolute bottom-4 left-4 right-4 flex items-center gap-2 text-white/95 hover:text-white group"
                data-testid="hero-image-house-link"
              >
                <MapPin className="w-4 h-4 text-white/90 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-white/80">Casa em destaque</div>
                  <div className="font-display text-xl truncate">{heroPick.house.name}</div>
                </div>
                <ArrowRight className="w-4 h-4 opacity-70 group-hover:translate-x-1 transition-transform ml-auto" />
              </Link>
            </div>
            )}
          </div>
        </div>

        {/* Buscador — flutua entre o Hero e a próxima seção */}
        <div className="relative -mt-20 md:-mt-24 z-10 px-6 lg:px-10">
          <div className="max-w-5xl mx-auto">
            <HeroSearch />
          </div>
          {/* Faixa discreta de confiança */}
          <div className="max-w-5xl mx-auto mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 text-xs text-[#22302E]/70">
            {[
              [ShieldCheck, "Casas selecionadas"],
              [CreditCard, "Pagamento seguro"],
              [FileText, "Contrato digital"],
              [MessageSquare, "Atendimento direto"],
            ].map(([Icon, label]) => (
              <div key={label} className="flex items-center gap-2 justify-center md:justify-start" data-testid={`trust-${slugTestId(label)}`}>
                <Icon className="w-4 h-4 text-[#0AA89C] shrink-0" aria-hidden="true" />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------- 2) Escolha a região + Vitrine -------------------- */}
      <section className="pt-20 px-6 lg:px-10" data-testid="destinations-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#066B63] font-semibold">Destinos</span>
              <h2 className="font-display text-3xl md:text-4xl text-[#22302E] mt-2">Escolha a região.</h2>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="dest-all"
              onClick={() => setCity("")}
              className={`px-4 py-2 rounded-full border text-sm transition-colors ${
                city === ""
                  ? "border-[#0AA89C] bg-[#0AA89C] text-white"
                  : "border-[#0AA89C]/30 text-[#066B63] hover:bg-[#0AA89C]/10"
              }`}
            >
              Todos
            </button>
            {DESTINATIONS.map((d) => (
              <button
                key={d.key}
                data-testid={`dest-${slugTestId(d.key)}`}
                onClick={() => setCity(d.key)}
                className={`px-4 py-2 rounded-full border text-sm transition-colors flex items-center gap-2 ${
                  city === d.key
                    ? "border-[#0AA89C] bg-[#0AA89C] text-white"
                    : "border-[#0AA89C]/30 text-[#066B63] hover:bg-[#0AA89C]/10"
                }`}
              >
                <MapPin className="w-3.5 h-3.5" />
                {d.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 px-6 lg:px-10" data-testid="featured-houses-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#066B63] font-semibold">Vitrine</span>
              <h2 className="font-display text-3xl md:text-4xl text-[#22302E] mt-2">
                {city ? `Casas em ${city}` : "Todas as casas"}
              </h2>
            </div>
            <Link
              to={city ? `/casas?city=${encodeURIComponent(city)}` : "/casas"}
              className="inline-flex items-center gap-2 text-sm text-[#066B63] font-medium hover:text-[#0AA89C]"
              data-testid="view-all-houses"
            >
              Ver todas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse h-96 bg-[#F7F4EF] rounded-md" />
              ))}
            </div>
          ) : featured.length === 0 ? (
            <div className="border border-dashed border-[#0AA89C]/30 rounded-md p-16 text-center text-[#22302E]/60">
              Nenhuma casa disponível em {city} no momento.
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {featured.map((h) => (
                <HouseCard key={h.id} house={h} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* -------------------- 3) Por que reservar na Azuhomes? -------------------- */}
      <section className="pt-16 pb-4 px-6 lg:px-10" data-testid="why-azuhomes-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-10">
            <span className="text-[11px] uppercase tracking-[0.28em] text-[#066B63] font-semibold">Reserva segura</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#22302E] mt-2">Por que reservar na Azuhomes?</h2>
            <p className="text-[#22302E]/70 mt-3">Da confirmação ao check-out, cada etapa é registrada, documentada e acompanhada.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              [ShieldCheck, "Reserva segura", "Toda a jornada da reserva é acompanhada e registrada."],
              [CreditCard, "Pagamento confiável", "Pagamento processado pelo Mercado Pago."],
              [FileText, "Contrato digital", "Documento vinculado à reserva e disponível para consulta."],
              [ClipboardCheck, "Check-in eletrônico", "Registro das condições do imóvel antes da entrada."],
              [HomeIcon, "Casas selecionadas", "Imóveis administrados ou selecionados pela equipe Azuhomes."],
              [MessageSquare, "Atendimento humanizado", "Suporte ao hóspede durante a jornada da hospedagem."],
            ].map(([Icon, title, desc]) => (
              <div key={title} className="bg-white border border-[#E9F5F3] rounded-lg p-5 hover:border-[#0AA89C]/40 transition-colors">
                <Icon className="w-6 h-6 text-[#0AA89C] mb-3" />
                <h3 className="font-medium text-[#22302E]">{title}</h3>
                <p className="text-sm text-[#22302E]/70 mt-1">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/reserva-segura" className="inline-flex items-center gap-1 text-[#0AA89C] hover:text-[#066B63] text-sm" data-testid="why-cta">
              Conheça a Reserva Segura Azuhomes <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* -------------------- 4) Value proposition (inalterado) -------------------- */}
      <section className="py-24 px-6 lg:px-10 bg-[#0AA89C] text-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-12">
          <ValueBlock
            title="Reserva direta"
            body="O preço apresentado é o preço final, sem intermediação de plataformas externas. Você fala diretamente com quem administra a casa."
          />
          <ValueBlock
            title="Padrão de atendimento"
            body="Cada chegada é preparada pela nossa equipe local, com protocolo próprio de limpeza, enxoval e conferência de itens."
          />
          <ValueBlock
            title="Pagamento seguro"
            body="Processamento via Mercado Pago, com opção de Pix ou cartão de crédito. Confirmação automática após a aprovação."
          />
        </div>
      </section>
      <LeadCaptureBanner />
    </PublicShell>
  );
}

function ValueBlock({ title, body }) {
  return (
    <div>
      <div className="w-8 h-px bg-white/60 mb-6" />
      <h3 className="font-display text-2xl">{title}</h3>
      <p className="text-white/85 mt-3 leading-relaxed">{body}</p>
    </div>
  );
}
